// Read-only full record for one service (v104): everything /api/jobs already returned
// for the row — no extra fetch. Rendered AFTER the Job Database sheet so Escape closes
// this one first and lands back on the search results.
import type { ComponentChildren } from 'preact';
import type { JobRow } from '../../state/adminUi';
import { editSheet, jobDetail } from '../../state/adminUi';
import { PhotoThumbs } from '../PhotoThumbs';
import { AdminSheet } from './AdminSheet';
import { StatusBadge } from './JobsDbSheet';

const digits = (phone: string) => phone.replace(/\D/g, '');

function Row({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="border-t border-slate-100 py-2 first:border-t-0">
      <p class="text-xs font-bold tracking-wide text-slate-400 uppercase">{label}</p>
      <div class="mt-0.5 text-[15px] text-navy-900">{children}</div>
    </div>
  );
}

export function JobDetailSheet({ job }: { job: JobRow }) {
  const close = () => (jobDetail.value = null);
  const when = [job.weekOf, job.dayLabel || job.day, job.slot].filter(Boolean).join(' · ');

  return (
    <AdminSheet
      title={job.development || job.address || 'Service call'}
      sub={when}
      onClose={close}
      testId="job-detail-sheet"
    >
      <div class="flex flex-wrap items-center gap-1.5 pb-1">
        <StatusBadge status={job.status} />
        {job.needsReturn && (
          <span class="db-badge bg-note-amber text-note-ink">Needs follow-up</span>
        )}
        {job.tech && (
          <span class="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-bold text-sky-800">
            {job.tech}
          </span>
        )}
      </div>

      {(job.development || job.lot || job.address) && (
        <Row label="Where">
          {job.development}
          {job.lot ? ` · Lot ${job.lot}` : ''}
          {job.address && (
            <>
              <br />
              <a
                class="font-medium text-brand-700 underline-offset-2 hover:underline"
                href={`https://maps.apple.com/?q=${encodeURIComponent(job.address)}`}
                target="_blank"
                rel="noreferrer"
              >
                {job.address}
              </a>
            </>
          )}
        </Row>
      )}

      {(job.name || digits(job.phone).length >= 7) && (
        <Row label="Contact">
          {job.name}
          {digits(job.phone).length >= 7 && (
            <>
              {job.name ? ' · ' : ''}
              <a class="font-semibold text-brand-700" href={`tel:${digits(job.phone)}`}>
                {job.phone}
              </a>
            </>
          )}
        </Row>
      )}

      {job.problem && (
        <Row label="Problem">
          <p class="whitespace-pre-wrap">{job.problem}</p>
        </Row>
      )}
      {job.instructions && (
        <Row label="Notes for tech">
          <p class="whitespace-pre-wrap rounded-lg bg-note-amber px-3 py-2 text-sm font-medium text-note-ink">
            {job.instructions}
          </p>
        </Row>
      )}

      {job.status !== 'active' && (
        <Row label="Completion">
          <p
            class={`font-semibold ${job.status === 'missed' ? 'text-missed-ink' : 'text-done-ink'}`}
          >
            {job.status === 'missed' ? 'Missed' : 'Done'}
            {job.completedBy && ` — ${job.completedBy}`}
            {job.completedAt &&
              ` · ${new Date(job.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`}
          </p>
          {job.note && <p class="mt-1 whitespace-pre-wrap text-slate-600">{job.note}</p>}
          <PhotoThumbs keys={job.photos} />
        </Row>
      )}

      <div class="mt-4 flex gap-2">
        <button class="btn-ghost flex-1" onClick={close}>
          Close
        </button>
        <button
          class="btn-primary flex-1"
          onClick={() =>
            (editSheet.value = {
              mode: 'edit',
              id: job.id,
              call: job,
              day: job.day,
              weekOf: job.weekOf,
              fromDb: true,
            })
          }
        >
          Edit service
        </button>
      </div>
    </AdminSheet>
  );
}
